import { Injectable, OnDestroy, inject } from '@angular/core';
import { User } from '../models/auth.models';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, of, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LocalStorageService, Tokens } from './local-storage.service';
import { EventType, Router } from '@angular/router';
@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {

    private router = inject(Router);

    private readonly lastRefresh = new BehaviorSubject<Date>((() => {
        const now = new Date();

        now.setDate(now.getDate() - 1);

        return now;
    })());
    private readonly user: BehaviorSubject<User>;
    private tokens$: BehaviorSubject<Tokens>;
    readonly user$: Observable<User>;
    private authenticating = false;

    // REFRESH profile info on page navigation if last refresh was more than X time ago
    private readonly refreshUserInfo$ = this.router.events.pipe(
        filter((event: any) => event.type === EventType.NavigationEnd),
        switchMap(() => {
            const lastRefresh = this.lastRefresh.value;

            const delta = new Date().getTime() - lastRefresh.getTime();

            if (delta / 1000 / 60 > 5) {
                return this.getProfile()
            }

            return of();
        })
    ).subscribe(() => { })

    constructor(
        private http: HttpClient,
        private storage: LocalStorageService,
    ) {

        this.user = new BehaviorSubject<User>(storage.getUser());
        this.tokens$ = new BehaviorSubject<Tokens>(storage.getTokens());

        this.user$ = this.user.asObservable();
    }

    ngOnDestroy(): void {
        this.refreshUserInfo$.unsubscribe()
    }

    isAuthenticating() {
        return this.authenticating;
    }

    hasAcessToken() {
        return !!this.tokens?.access_token;
    }

    public get tokens(): Tokens {
        return this.tokens$.value;
    }

    processGrantToken(accessToken: string) {
        this.authenticating = true
        return this.http.post(environment.api.account + 'auth/token/access', {
            code: accessToken,
        }).pipe(
            map((tokens: any) => {
                this.storage.saveTokens(tokens);

                this.tokens$.next(tokens);

                return tokens;
            }),
            tap({
                next: () => this.authenticating = false,
                error: () => this.authenticating = false
            }),
            switchMap(() => this.getProfile())
        )
    }

    getProfile(): Observable<any> {
        return this.http.get(environment.api.account + 'profile').pipe(
            tap((info: any) => {
                this.lastRefresh.next(new Date());
                const user = {
                    firstName: info.firstName,
                    lastName: info.lastName,
                    email: info.email,
                    active: info.active,
                }

                this.user.next(user);
                this.storage.saveUser(user);
            })
        );
    }

    /**
     * Returns the current user
     */
    public currentUser(): any {
        return this.user.value;
    }

    /**
     * Logout the user
     */
    logout() {
        // TODO: implement auth/token/revoke
        this.storage.clear()
        this.user.next(null!);

        this.router.navigate(['/welcome']);
    }

    refreshToken(): Observable<Tokens> {
        const refreshToken = this.tokens?.refresh_token;

        if (refreshToken) {
            return this.http.post(`${environment.api.account}auth/token`, { token: refreshToken }).pipe(
                tap((tokens: any) => {
                    this.storage.saveTokens(tokens);

                    this.tokens$.next(tokens);
                })
            );
        }

        return throwError(() => ({ message: 'Unauthorized', statusCode: 401 }));
    }

    requestActivationEmail() {
        return this.http.post(`${environment.api.account}auth/activation`, {
            email: this.currentUser().email
        });
    }

}

